import { FC, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import styled from 'styled-components'
import {
  NewsItemComment_NewsCommentEdgeFragment,
  useCurrentUserQuery,
  useGqlUnMuteUserMutation,
} from 'generated/graphql'
import AlreadyReportedModal from '@app.feature/channel/component/modals/AlreadyReportedModal'
import BanUserModal from '@app.feature/channel/component/modals/BanUserModal'
import DeleteCommentModal from '@app.feature/channel/component/modals/DeleteCommentModal'
import ReportModal from '@app.feature/channel/component/modals/ReportModal'
import useChannelError from '@app.feature/channel/hook/useChannelError'
import useDropdown from '@app.modules/hooks/useDropdown'
import MoreDropdownBtn from 'app.feature/channel/component/MoreDropdownBtn'
import LoginAlert from 'components/LoginAlert'

type TProps = {
  data: NewsItemComment_NewsCommentEdgeFragment
  onEditMenuClick: () => void
  onDeleteMenuClick: () => void
}

const DropdownBtn: FC<TProps> = ({
  data,
  onEditMenuClick,
  onDeleteMenuClick,
}) => {
  const currentUserQuery = useCurrentUserQuery()
  const [menuRef, isDropdownOpened, handleDropDownBtn] = useDropdown()
  const { enqueueSnackbar } = useSnackbar()
  const onError = useChannelError()

  const [reportModalopen, setReportModalopen] = useState(false)
  const [deleteModalopen, setDeleteModalOpen] = useState(false)
  const [banModalopen, setBanModalOpen] = useState(false)
  const [loginModalOpened, setLoginModalOpened] = useState(false)

  const unMuteUserMutation = useGqlUnMuteUserMutation({
    // update: unMuteUserUpdater(data.node.id),
    onCompleted: () => {
      enqueueSnackbar('차단이 해제되었습니다.')
    },
    onError: onError,
  })

  const fetchUnMuteUser = () => {
    if (onError()) return

    if (!data.node.author) return
    const [mutate] = unMuteUserMutation
    mutate({
      variables: {
        input: {
          mutedUserId: data.node.author.id,
          newsCommentId: data.node.id,
        },
      },
    })
  }

  const menus = useMemo<
    {
      label: string
      handleClick: () => void
    }[]
  >(() => {
    const menus: {
      label: string
      handleClick: () => void
    }[] = []
    // 신고
    if (!data.node.viewerIsAuthor) {
      menus.push({
        label: '댓글 신고',
        handleClick: () => {
          if (onError()) return
          if (!currentUserQuery.data?.currentUser)
            return setLoginModalOpened(true)
          setReportModalopen(true)
        },
      })
    }
    // 차단 해제
    if (!data.node.viewerIsAuthor && data.node.author?.__typename === 'User') {
      if (data.node.viewerHasMuteAuthor) {
        menus.push({
          label: '회원 차단 해제',
          handleClick: fetchUnMuteUser,
        })
      }
      // 차단
      else {
        menus.push({
          label: '회원 차단',
          handleClick: () => {
            if (onError()) return
            setBanModalOpen(true)
          },
        })
      }
    }
    // 수정
    if (data.node.viewerIsAuthor) {
      menus.push({
        label: '댓글 수정',
        handleClick: () => {
          if (onError()) return

          onEditMenuClick()
        },
      })
    }
    // 삭제
    if (data.node.viewerCanDelete) {
      menus.push({
        label: '댓글 삭제',
        handleClick: () => {
          if (onError()) return
          setDeleteModalOpen(true)
        },
      })
    }

    return menus
  }, [data, currentUserQuery.data])

  return (
    <StyledWrapper>
      <div
        ref={menuRef}
        onClick={handleDropDownBtn}
        data-open={isDropdownOpened}
      >
        <MoreDropdownBtn className="more-menu-btn" />
      </div>
      <div className="more-menu-list" data-open={isDropdownOpened}>
        {menus.map((menu, idx) => (
          <button className="more-menu" key={idx} onClick={menu.handleClick}>
            {menu.label}
          </button>
        ))}
      </div>
      {data.node.viewerHasReported && (
        <AlreadyReportedModal
          type="comment"
          open={reportModalopen}
          onClose={() => setReportModalopen(false)}
        />
      )}
      {!data.node.viewerHasReported && (
        <ReportModal
          type="comment"
          open={reportModalopen}
          reportedId={data.node.id}
          onClose={() => setReportModalopen(false)}
        />
      )}
      <BanUserModal
        open={banModalopen}
        data={data}
        onClose={() => setBanModalOpen(false)}
      />
      <DeleteCommentModal
        open={deleteModalopen}
        id={data.node.id}
        onClose={() => setDeleteModalOpen(false)}
        onCompleted={onDeleteMenuClick}
      />
      <LoginAlert
        DialogProps={{
          open: loginModalOpened,
        }}
        onRequestClose={() => setLoginModalOpened(false)}
      />
    </StyledWrapper>
  )
}

export default DropdownBtn

const StyledWrapper = styled.div`
  position: relative;
  .more-menu-btn {
    width: 28px;
    height: 28px;
    .dot {
      width: 3.5px;
      height: 3.5px;
    }
  }
  .more-menu-list {
    z-index: 1;
    display: none;
    position: absolute;
    top: calc(100% + 5px);
    right: 0;
    border: 1px solid #cccccc;
    box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.16);
    border-radius: 5px;
    padding: 6px 0;
    text-align: center;
    white-space: nowrap;
    font-size: 12px;
    background-color: white;
    .more-menu {
      display: block;
      border: none;
      background: none;
      padding: 4px 12px;
      &:hover {
        background-color: #f8f8f8;
      }
    }
    &[data-open='true'] {
      display: block;
    }
  }
`
